import { Button } from "@mui/material"
import { createEvent } from "library/local-events/index.mjs"
import { confirm } from "lib/@dialogs/confirm"
import { confirmByUserInput } from "lib/@dialogs/confirm-by-user-input"
import { memo, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { updateZeroUBatch } from "routes/regime/plugins/zero-u-wizard/controller/update-zero-u"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { SuppressTreeBusy } from "routes/regime/plugins/zero-u-wizard/suppress-tree-busy"
import { RegimeDraftLockHide } from "routes/regime/regime-draft-locking/regime-draft-lock-hide"

export const ApplyChanged = createEvent("ZeroUApplyChanged")
export const ChangePage = createEvent("ZeroUChangePage", {
    expose: "check",
    isAsync: true,
    extract: (v) => v[0].canChange,
    parameters: () => ({ canChange: true }),
})
export const CanApply = createEvent("ZeroUCanApply", {
    expose: "check",
    parameters: () => ({ canApply: false, tasksForCount: [] }),
    extract: (v) => ({ canApply: v[0].canApply, tasksForCount: v[0].tasksForCount }),
})
// Collect pending changes from all Tailor0U components for batch update
export const CollectPendingChanges = createEvent("ZeroUCollectPendingChanges", {
    expose: "check",
    parameters: () => ({ updates: [] }),
    extract: (v) => ({ updates: v[0].updates }),
})
export const ApplyBusy = createEvent("ZeroUApplyBusy")
export const TaskUpdated = createEvent("ZeroUTaskUpdated")
// Signal that batch update is starting (so rows can show spinners)
export const BatchUpdateStarted = createEvent("ZeroUBatchUpdateStarted")
// Signal that batch update completed successfully
export const BatchUpdateCompleted = createEvent("ZeroUBatchUpdateCompleted")

export const ApplyAllButton = memo(ApplyAllButtonInner)

function ApplyAllButtonInner() {
    ApplyChanged.useRefresh()
    ChangePage.useEvent(confirmAbandon)
    const [busy, setBusy] = useState(false)
    ApplyBusy.useEvent(({ busy }) => setBusy(!!busy), [])
    const { search, group, regimeId } = useBoundContext()

    const checkResult = CanApply.check()
    const disabled = !checkResult.canApply || busy
    const count = checkResult.tasksForCount.filter(Boolean).length

    return (
        <RegimeDraftLockHide>
            <Button disabled={disabled} onClick={apply} size="small" data-cy="btn-apply-all">
                Apply All
            </Button>
        </RegimeDraftLockHide>
    )

    async function confirmAbandon() {
        if (CanApply.check().canApply && !(await confirm("Abandon all of the changes you made?", "Abandon Changes"))) {
            return { canChange: false }
        }
        return undefined
    }

    async function apply() {
        if (
            !(await confirmByUserInput({
                title: `Are you sure you want to apply all of the changes?`,
                confirmValue: count,
            }))
        ) {
            return
        }

        // Collect all pending changes from Tailor0U components
        const { updates } = CollectPendingChanges.check()
        if (!updates.length) return

        // Signal that batch is starting so rows can show their spinners
        // Pass full update info so rows can match by task + schedule + skill
        BatchUpdateStarted.raise({ updates })

        SuppressTreeBusy.raise(true)
        try {
            await updateZeroUBatch({ regimeId, group, search, updates })
            // Signal that batch completed so all updated tasks can be removed from UI
            BatchUpdateCompleted.raise({ updates })
        } catch (error) {
            console.error("Batch update failed:", error)
            errorSnackbar("Failed to update Unspecified Frequencies")
            // Signal completion even on error so rows can reset their state
            BatchUpdateCompleted.raise({ updates: [], failed: true })
        } finally {
            SuppressTreeBusy.raise(false)
        }
    }
}
